"use client";

import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import { Tip } from '@/components/fun-ui/Tip';
import { CircleChevronDownIcon, InfoIcon, MoreHorizontalIcon, XIcon } from '@/components/fun-ui/icons';
import { useBenefitsCompare } from '@/stores/appStore';
import type { BenefitsCompareColumnKey, BenefitsCompareConfig, BenefitsCompareRow } from '@/stores/slices/commonSlice';

export type BenefitsCompareVariant = 'memberPlus' | 'memberPlus3x' | 'recharge';

/** 权益表正文与 tips 图标色（与单列单元格正文一致） */
const BENEFITS_CELL_TEXT = '#3D3D3D';
/** 会员表 Plus 列前三行及「飞豆」行 tips 图标色 */
const BENEFITS_PLUS_ACCENT = '#987E61';
const BENEFITS_TIP_ICON_COLOR = 'hsl(var(--foreground))';
const PAYMENT_LEARN_MORE_URL = 'https://nfj2hhi1cw.feishu.cn/wiki/WIKtwWj2Fir5W5kpf8LcVxRLn1e';

const BENEFIT_COLUMN_ORDER_BY_VARIANT: Record<BenefitsCompareVariant, BenefitsCompareColumnKey[]> = {
  memberPlus: ['plus', 'plus3x', 'basic'],
  memberPlus3x: ['plus3x', 'plus', 'basic'],
  recharge: ['basic', 'plus', 'plus3x'],
};

function normalizeColumnOrder(
  value: BenefitsCompareColumnKey[] | undefined,
  fallback: BenefitsCompareColumnKey[],
): BenefitsCompareColumnKey[] {
  if (!value?.length) {
    return fallback;
  }
  const validColumns = new Set<BenefitsCompareColumnKey>(['basic', 'plus', 'plus3x']);
  const normalized = value.filter((column) => validColumns.has(column));
  return normalized.length ? normalized : fallback;
}

function renderBenefitCell(content: string, scale: number): ReactNode {
  if (content === 'X') {
    return (
      <XIcon
        size={Math.round(16 * scale)}
        color="#E70000"
        aria-hidden
      />
    );
  }
  if (content === '...') {
    return (
      <MoreHorizontalIcon
        size={Math.round(16 * scale)}
        color={BENEFITS_CELL_TEXT}
        aria-hidden
      />
    );
  }
  return content;
}

function getCellTip(row: BenefitsCompareRow, column: BenefitsCompareColumnKey): string | undefined {
  if (row.tips?.[column]) {
    return row.tips[column];
  }
  if (column === 'basic') {
    return row.basicTip;
  }
  if (column === 'plus') {
    return row.plusTip;
  }
  return row.plus3xTip;
}

function renderColumnCell(row: BenefitsCompareRow, column: BenefitsCompareColumnKey, scale: number): ReactNode {
  const content = row[column];
  const tip = getCellTip(row, column);
  const main = renderBenefitCell(content, scale);
  if (!tip) {
    return main;
  }
  const iconSize = Math.round(10 * scale);
  return (
    <span className="inline-flex max-w-full flex-nowrap items-center gap-1 whitespace-nowrap align-middle">
      {main}
      <Tip
        label={tip}
        side="top"
        sideOffset={6}
        className="inline-flex shrink-0 cursor-help"
        contentClassName="max-w-[min(280px,85vw)] whitespace-pre-line break-words px-3 py-2 text-xs font-normal leading-relaxed text-primary-foreground"
      >
        <InfoIcon size={iconSize} className="block cursor-help" color={BENEFITS_TIP_ICON_COLOR} />
      </Tip>
    </span>
  );
}

export type PaymentBenefitsCompareTableProps = {
  scale?: number;
  variant: BenefitsCompareVariant;
  className?: string;
  isActive?: boolean;
};

/** 竖向列分割线宽（随 scale，基准 0.9px @ 1100×759） */
function colDividerWidth(scale: number) {
  return `${0.9 * scale}px`;
}

/** 表格外轮廓圆角（设计稿 1100×760 下 10px） */
function outerCompareTableRadius(scale: number) {
  return `${10 * scale}px`;
}

/** 会员表：列竖线 — linear-gradient(90deg, …) 沿线条横向淡出 */
function memberColDividerStyle(scale: number): CSSProperties {
  const w = colDividerWidth(scale);
  return {
    backgroundImage:
      'linear-gradient(90deg, #F3E7DA 0%, rgba(243, 231, 218, 0.34) 100%)',
    backgroundSize: `${w} 100%`,
    backgroundPosition: 'left center',
    backgroundRepeat: 'no-repeat',
  };
}

/** 充值表：列竖线 0.9px #D8E1FC（设计稿 rotate 语义由竖线 border-left 实现） */
function rechargeColDividerStyle(scale: number): CSSProperties {
  return {
    borderLeftWidth: colDividerWidth(scale),
    borderLeftStyle: 'solid',
    borderLeftColor: '#D8E1FC',
  };
}

export function PaymentBenefitsCompareTable({
  scale = 1,
  variant,
  className,
  isActive = true,
}: PaymentBenefitsCompareTableProps) {
  const { value: remoteConfig, fetch, loading } = useBenefitsCompare();
  const [remoteRequested, setRemoteRequested] = useState(false);
  const [backupConfig, setBackupConfig] = useState<BenefitsCompareConfig | null>(null);

  const config = remoteConfig?.rows?.length ? remoteConfig : backupConfig;

  useEffect(() => {
    if (!isActive || remoteConfig?.rows?.length || loading || remoteRequested) {
      return;
    }

    let cancelled = false;
    fetch().finally(() => {
      if (!cancelled) {
        setRemoteRequested(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [fetch, isActive, loading, remoteConfig?.rows?.length, remoteRequested]);

  useEffect(() => {
    if (!isActive || remoteConfig?.rows?.length || loading || !remoteRequested || backupConfig) {
      return;
    }

    let cancelled = false;
    import('@/config/backup/benefits_compare.json').then((module) => {
      if (!cancelled) {
        setBackupConfig(module.default as BenefitsCompareConfig);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [backupConfig, isActive, loading, remoteConfig?.rows?.length, remoteRequested]);

  if (!config?.rows?.length) {
    return null;
  }

  const benefitRows = config.rows;
  const headers = {
    feature: '权益对比',
    basic: '基础',
    plus: 'Plus 会员',
    plus3x: 'Plus 3倍会员',
    ...config.headers,
  };
  const learnMoreHref = config.learnMoreUrl || PAYMENT_LEARN_MORE_URL;
  const sectionBreakRowIndex =
    typeof config.sectionBreakRowIndex === 'number'
      ? config.sectionBreakRowIndex
      : 3;

  const isMemberLayout = variant === 'memberPlus' || variant === 'memberPlus3x';
  const columnOrder = normalizeColumnOrder(
    config.columnOrders?.[variant],
    BENEFIT_COLUMN_ORDER_BY_VARIANT[variant],
  );

  const fsTitle = 14.4 * scale;
  const fsCell = 12.6 * scale;
  const lhTitle = 20 * scale;
  const lhCell = 19 * scale;
  const padH = `${10 * scale}px ${12 * scale}px`;
  const cellPadX = 12 * scale;
  const cellPadYDefault = 6.5 * scale;
  const cellPadYEmphasis = 11 * scale;
  const borderSectionMember = '#F3E7DA';
  const borderFooterMember = '#FBF7F2';
  const borderSectionRecharge = '#D8E1FC';
  const borderFooterRecharge = '#EAF0FD';

  const textDefault = '#3D3D3D';
  const textPlusTop = BENEFITS_PLUS_ACCENT;
  const getCellVerticalPadding = (rowIndex: number) => {
    const isFirstRow = rowIndex === 0;
    const isBeforeSectionBreak = rowIndex === sectionBreakRowIndex - 1;
    const isSectionBreakRow = rowIndex === sectionBreakRowIndex;
    const isLastRow = rowIndex === benefitRows.length - 1;
    return {
      top: (isFirstRow || isSectionBreakRow) ? cellPadYEmphasis : cellPadYDefault,
      bottom: (isBeforeSectionBreak || isLastRow) ? cellPadYEmphasis : cellPadYDefault,
    };
  };

  if (isMemberLayout) {
    const memberHeadCells = columnOrder.map((key) => ({
      key,
      label: headers[key],
    }));

    const headBg = '#F6EFE8';
    const headText = '#82674D';
    const col1BodyBg = 'rgba(246, 239, 232, 0.4)';

    const cellBase = (rowIndex: number): CSSProperties => {
      const sectionBreak = rowIndex === sectionBreakRowIndex;
      const cellPadding = getCellVerticalPadding(rowIndex);
      return {
        padding: `${cellPadding.top}px ${cellPadX}px ${cellPadding.bottom}px`,
        fontSize: `${fsCell}px`,
        lineHeight: `${lhCell}px`,
        color: textDefault,
        fontWeight: 400,
        backgroundColor: 'transparent',
        borderStyle: 'solid',
        borderColor: 'transparent',
        borderWidth: 0,
        borderTopWidth: sectionBreak ? colDividerWidth(scale) : 0,
        borderTopStyle: sectionBreak ? 'solid' : undefined,
        borderTopColor: sectionBreak ? borderSectionMember : 'transparent',
      };
    };

    const plusCellStyle = (rowIndex: number, withDivider: boolean): CSSProperties => {
      const top = rowIndex < 3;
      return {
        ...cellBase(rowIndex),
        ...(withDivider ? memberColDividerStyle(scale) : {}),
        color: top ? textPlusTop : textDefault,
        fontWeight: top ? 500 : 400,
      };
    };

    return (
      <div
        className={cn('flex w-full flex-col overflow-hidden bg-background', className)}
        style={{ borderRadius: outerCompareTableRadius(scale) }}
      >
        <div className="w-full min-w-0 shrink-0 overflow-x-hidden">
          <table
            className="w-full border-collapse"
            style={{ tableLayout: 'fixed' }}
          >
            <colgroup>
              <col style={{ width: '24%' }} />
              <col style={{ width: '30%' }} />
              <col style={{ width: '30%' }} />
              <col style={{ width: '16%' }} />
            </colgroup>
            <thead>
              <tr>
                <th
                  className="text-center whitespace-nowrap"
                  style={{
                    padding: padH,
                    fontSize: `${fsTitle}px`,
                    lineHeight: `${lhTitle}px`,
                    fontWeight: 500,
                    color: headText,
                    backgroundColor: headBg,
                  }}
                >
                  {headers.feature}
                </th>
                {memberHeadCells.map((h) => (
                  <th
                    key={h.key}
                    className={cn(
                      'text-center',
                      h.key !== 'basic' && 'whitespace-nowrap',
                    )}
                    style={{
                      padding: padH,
                      fontSize: `${fsTitle}px`,
                      lineHeight: `${lhTitle}px`,
                      fontWeight: 500,
                      color: headText,
                      backgroundColor: headBg,
                      ...memberColDividerStyle(scale),
                    }}
                  >
                    {h.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {benefitRows.map((row, rowIndex) => {
                const td1: CSSProperties = {
                  ...cellBase(rowIndex),
                  backgroundColor: col1BodyBg,
                };
                return (
                  <tr key={row.feature}>
                    <td className="text-left whitespace-nowrap" style={td1}>
                      {row.feature}
                    </td>
                    {columnOrder.map((column) => (
                      <td
                        key={column}
                        className="text-left whitespace-nowrap"
                        style={
                          column === 'basic'
                            ? {
                              ...cellBase(rowIndex),
                              ...memberColDividerStyle(scale),
                            }
                            : plusCellStyle(rowIndex, true)
                        }
                      >
                        {renderColumnCell(row, column, scale)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div
          className="relative z-20 w-full shrink-0 bg-background text-center pointer-events-auto"
          style={{
            padding: `${8 * scale}px ${12 * scale}px`,
            borderTop: `${colDividerWidth(scale)} solid ${borderFooterMember}`,
          }}
        >
          <Link
            href={learnMoreHref}
            target="_blank"
            className="inline-flex items-center text-[#3D3D3D] hover:text-primary hover:underline"
            style={{
              fontSize: `${fsCell}px`,
              lineHeight: `${lhCell}px`,
              fontWeight: 400,
              gap: `${4 * scale}px`,
            }}
          >
            了解更多
            <CircleChevronDownIcon
              size={Math.round(18 * scale)}
              color={textDefault}
              className="shrink-0"
              aria-hidden
            />
          </Link>
        </div>
      </div>
    );
  }

  const headBgR = '#DDE6FE';
  const headTextR = '#4E5969';
  const col1BodyBgR = 'rgba(221, 230, 254, 0.3)';

  const cellBaseR = (rowIndex: number): CSSProperties => {
    const sectionBreak = rowIndex === sectionBreakRowIndex;
    const cellPadding = getCellVerticalPadding(rowIndex);
    return {
      padding: `${cellPadding.top}px ${cellPadX}px ${cellPadding.bottom}px`,
      fontSize: `${fsCell}px`,
      lineHeight: `${lhCell}px`,
      color: textDefault,
      fontWeight: 400,
      backgroundColor: 'transparent',
      borderStyle: 'solid',
      borderColor: 'transparent',
      borderWidth: 0,
      borderTopWidth: sectionBreak ? colDividerWidth(scale) : 0,
      borderTopStyle: sectionBreak ? 'solid' : undefined,
      borderTopColor: sectionBreak ? borderSectionRecharge : 'transparent',
    };
  };

  const plusCellStyleR = (rowIndex: number, withDivider: boolean): CSSProperties => {
    const top = rowIndex < 3;
    return {
      ...cellBaseR(rowIndex),
      ...(withDivider ? rechargeColDividerStyle(scale) : {}),
      color: top ? textPlusTop : textDefault,
      fontWeight: top ? 500 : 400,
    };
  };

  const rechargeTableBg =
    'linear-gradient(180deg, #E9EFFD 0%, rgba(233, 239, 253, 0) 119%)';

  return (
    <div
      className={cn('flex h-full min-h-0 flex-col overflow-hidden bg-background', className)}
      style={{ borderRadius: outerCompareTableRadius(scale) }}
    >
      <div
        className="min-h-0 w-full flex-1 overflow-x-auto bg-background"
        style={{ backgroundImage: rechargeTableBg }}
      >
        <table className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '24%' }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: '30%' }} />
            <col style={{ width: '30%' }} />
          </colgroup>
          <thead>
            <tr>
              <th
                className="text-center whitespace-nowrap"
                style={{
                  padding: padH,
                  fontSize: `${fsTitle}px`,
                  lineHeight: `${lhTitle}px`,
                  fontWeight: 500,
                  color: headTextR,
                  backgroundColor: headBgR,
                }}
              >
                {headers.feature}
              </th>
              {columnOrder.map((column) => (
                <th
                  key={column}
                  className={cn(
                    'text-center',
                    column !== 'basic' && 'whitespace-nowrap',
                  )}
                  style={{
                    padding: padH,
                    fontSize: `${fsTitle}px`,
                    lineHeight: `${lhTitle}px`,
                    fontWeight: 500,
                    color: headTextR,
                    backgroundColor: headBgR,
                    ...rechargeColDividerStyle(scale),
                  }}
                >
                  {headers[column]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {benefitRows.map((row, rowIndex) => {
              const td1: CSSProperties = {
                ...cellBaseR(rowIndex),
                backgroundColor: col1BodyBgR,
              };
              return (
                <tr key={row.feature}>
                  <td className="text-left whitespace-nowrap" style={td1}>
                    {row.feature}
                  </td>
                  {columnOrder.map((column) => (
                    <td
                      key={column}
                      className="text-left whitespace-nowrap"
                      style={
                        column === 'basic'
                          ? {
                            ...cellBaseR(rowIndex),
                            ...rechargeColDividerStyle(scale),
                          }
                          : plusCellStyleR(rowIndex, true)
                      }
                    >
                      {renderColumnCell(row, column, scale)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div
        className="relative z-20 w-full shrink-0 bg-transparent text-center pointer-events-auto"
        style={{
          padding: `${8 * scale}px ${12 * scale}px`,
          borderTop: `${colDividerWidth(scale)} solid ${borderFooterRecharge}`,
        }}
      >
        <Link
          href={learnMoreHref}
          target="_blank"
          className="inline-flex items-center text-[#3D3D3D] hover:text-primary hover:underline"
          style={{
            fontSize: `${fsCell}px`,
            lineHeight: `${lhCell}px`,
            fontWeight: 400,
            gap: `${4 * scale}px`,
          }}
        >
          了解更多
          <CircleChevronDownIcon
            size={Math.round(18 * scale)}
            color={textDefault}
            className="shrink-0"
            aria-hidden
          />
        </Link>
      </div>
    </div>
  );
}
